import { useEffect, useState, useMemo, useRef } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  ArrowUp,
  ArrowDown,
  Clock,
  CheckCircle2,
  Package,
  Users,
  AlertCircle,
} from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { CustomButton } from '@/components/molecules/CustomButton';
import { PokemonPicker } from '@/components/molecules/PokemonPicker';
import { SelectedPokemonPanel } from '@/components/molecules/SelectedPokemonPanel';
import { EvolutionModal } from '@/components/molecules/EvolutionModal';
import { LearnMoveModal } from '@/components/molecules/LearnMoveModal';
import { Loader } from '@/components/atoms/Loader';
import { useMeUser } from '@/hooks/useUser';
import { useToast } from '@/hooks/useToastContext';
import { type OwnedPokemon } from '@/services/pokemon';
import {
  getMyPension,
  getAllPensions,
  depositPokemon,
  withdrawPokemon,
  useSablier,
  type MyPension,
  type PensionEntry,
  type WithdrawPensionResponse,
  type AllPensionsSortBy,
  type AllPensionsSortOrder,
} from '@/services/pension';
import { getInventory } from '@/services/user';
import { cdnUrl } from '@/utils/cdn';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

const formatCountdown = (ms: number): string => {
  if (ms <= 0) return '0s';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

// ─── usePensionCountdown ──────────────────────────────────────────────────────

const usePensionCountdown = (autoReleaseAt: string) => {
  const getMs = () => Math.max(0, new Date(autoReleaseAt).getTime() - Date.now());
  const [msLeft, setMsLeft] = useState(getMs);
  useEffect(() => {
    setMsLeft(getMs());
    const id = setInterval(() => setMsLeft(getMs()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReleaseAt]);
  return msLeft;
};

// ─── usePaddockWalkers ────────────────────────────────────────────────────────

interface WalkState {
  x: number;
  y: number;
  facing: 'left' | 'right';
  duration: number;
}

const usePaddockWalkers = (ids: string[]): Record<string, WalkState> => {
  const idsKey = ids.join(',');
  const [states, setStates] = useState<Record<string, WalkState>>({});

  useEffect(() => {
    if (ids.length === 0) {
      setStates({});
      return;
    }

    setStates(prev => {
      const next: Record<string, WalkState> = {};
      ids.forEach(id => {
        next[id] = prev[id] ?? {
          x: rand(3, 84),
          y: rand(5, 62),
          facing: 'right',
          duration: 0,
        };
      });
      return next;
    });

    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const move = (id: string) => {
      const delay = rand(2000, 6500);
      const t = setTimeout(() => {
        setStates(prev => {
          const cur = prev[id];
          if (!cur) return prev;
          const nx = rand(3, 84);
          const ny = rand(5, 62);
          return {
            ...prev,
            [id]: {
              x: nx,
              y: ny,
              facing: nx < cur.x ? 'left' : 'right',
              duration: rand(2500, 5500),
            },
          };
        });
        move(id);
      }, delay);
      timeouts.push(t);
    };

    ids.forEach((id, i) => {
      const t = setTimeout(() => move(id), i * 130 + rand(0, 500));
      timeouts.push(t);
    });

    return () => timeouts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return states;
};

// ─── PensionPaddock ───────────────────────────────────────────────────────────

const FENCE_POSTS = 13;

interface PaddockProps {
  entries: PensionEntry[];
  total: number;
  meUserId?: string;
}

const PensionPaddock = ({ entries, total, meUserId }: PaddockProps) => {
  const ids = useMemo(() => entries.map(e => e.pensionId), [entries]);
  const walkers = usePaddockWalkers(ids);

  const shinyCount = entries.filter(e => e.ownedPokemon.isShiny).length;
  const completedCount = entries.filter(e => e.completed).length;

  return (
    <div
      className="overflow-hidden rounded-3xl"
      style={{ border: '4px solid #92400e', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
    >
      {/* ── Scene ── */}
      <div
        className="relative overflow-hidden"
        style={{
          height: 380,
          userSelect: 'none',
          background:
            'linear-gradient(180deg,#7dd3fc 0%,#bae6fd 16%,#e0f2fe 36%,#fef9c3 56%,#bbf7d0 76%,#4ade80 100%)',
        }}
      >
        {/* Sun */}
        <div
          className="absolute"
          style={{
            top: 20,
            right: 30,
            width: 50,
            height: 50,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 38% 38%,#fef08a,#fbbf24 60%)',
            boxShadow:
              '0 0 0 12px rgba(251,191,36,0.14),0 0 0 26px rgba(251,191,36,0.07)',
          }}
        />

        {/* Cloud 1 */}
        <div
          className="absolute"
          style={{ top: 22, left: 26, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.07))' }}
        >
          <div style={{ width: 72, height: 26, background: 'rgba(255,255,255,0.93)', borderRadius: 999 }} />
          <div style={{ position: 'absolute', top: -12, left: 16, width: 44, height: 36, background: 'rgba(255,255,255,0.93)', borderRadius: 999 }} />
          <div style={{ position: 'absolute', top: -7, left: 38, width: 28, height: 28, background: 'rgba(255,255,255,0.93)', borderRadius: 999 }} />
        </div>

        {/* Cloud 2 */}
        <div
          className="absolute"
          style={{ top: 30, right: 100, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.05))' }}
        >
          <div style={{ width: 56, height: 20, background: 'rgba(255,255,255,0.8)', borderRadius: 999 }} />
          <div style={{ position: 'absolute', top: -9, left: 10, width: 36, height: 28, background: 'rgba(255,255,255,0.8)', borderRadius: 999 }} />
        </div>

        {/* Ground shadow */}
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0"
          style={{ height: 80, background: 'linear-gradient(to top,rgba(0,0,0,0.09),transparent)' }}
        />

        {/* ── Fence ── */}
        <div className="absolute left-0 right-0" style={{ bottom: 36 }}>
          <div
            style={{
              position: 'absolute',
              left: 0, right: 0,
              top: -20,
              height: 11,
              background: 'linear-gradient(180deg,#b45309,#92400e)',
              boxShadow: '0 3px 7px rgba(0,0,0,0.25)',
              borderRadius: 3,
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0, right: 0,
              top: -7,
              height: 8,
              background: 'linear-gradient(180deg,#92400e,#78350f)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              borderRadius: 2,
            }}
          />
          {Array.from({ length: FENCE_POSTS }).map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                bottom: 0,
                left: `${(i / (FENCE_POSTS - 1)) * 100}%`,
                transform: 'translateX(-50%)',
                width: 13,
                height: 44,
                background: 'linear-gradient(90deg,#78350f 0%,#b45309 45%,#92400e 100%)',
                borderRadius: '3px 3px 0 0',
                boxShadow: '2px 0 5px rgba(0,0,0,0.18)',
              }}
            />
          ))}
        </div>

        {/* ── Walking Pokémon ── */}
        {entries.map(entry => {
          const state = walkers[entry.pensionId];
          if (!state) return null;

          const isOwn = entry.userId === meUserId;
          const gifUrl = cdnUrl(
            entry.ownedPokemon.isShiny
              ? entry.pokemon.spriteAnimationShiny
              : entry.pokemon.spriteAnimation,
          );

          const depthT = Math.min(1, Math.max(0, (state.y - 5) / 57));
          const scale = 1.1 - depthT * 0.65;
          const scaleX = state.facing === 'left' ? -scale : scale;
          const zIndex = Math.round(70 - state.y);
          const spriteVisualHeight = Math.round(scale * 64);

          const hasNickname =
            entry.ownedPokemon.nickname &&
            entry.ownedPokemon.nickname !== entry.pokemon.realname;

          return (
            <div
              key={entry.pensionId}
              className="absolute group"
              style={{
                left: `${state.x}%`,
                bottom: `${state.y}%`,
                transform: 'translateX(-50%)',
                zIndex,
                transition:
                  state.duration > 0
                    ? `left ${state.duration}ms ease-in-out, bottom ${state.duration}ms ease-in-out`
                    : 'none',
              }}
            >
              {isOwn && (
                <div
                  className="pointer-events-none absolute"
                  style={{
                    inset: 0,
                    background:
                      'radial-gradient(circle,rgba(242,150,2,0.55) 0%,transparent 70%)',
                    transform: 'scale(2.4)',
                    zIndex: -1,
                  }}
                />
              )}

              <img
                src={gifUrl}
                alt={entry.pokemon.realname}
                className="block h-16 w-16 object-contain"
                style={{
                  transform: `scale(${scaleX},${scale})`,
                  transformOrigin: 'center bottom',
                  imageRendering: 'pixelated',
                  filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.22))',
                }}
                onError={e => {
                  e.currentTarget.style.opacity = '0';
                }}
              />

              <div
                className="pointer-events-none absolute"
                style={{
                  bottom: -3,
                  left: '50%',
                  width: `${scale * 50}px`,
                  height: `${scale * 9}px`,
                  background:
                    'radial-gradient(ellipse,rgba(0,0,0,0.25) 0%,transparent 70%)',
                  transform: 'translateX(-50%)',
                  borderRadius: '50%',
                }}
              />

              <div
                className="pointer-events-none absolute flex items-center gap-0.5"
                style={{
                  bottom: `${spriteVisualHeight + 4}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                {isOwn && (
                  <span
                    style={{
                      background: '#f29602',
                      color: '#fff',
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '1px 5px',
                      borderRadius: 999,
                      lineHeight: '14px',
                    }}
                  >
                    Moi
                  </span>
                )}
                {entry.ownedPokemon.isShiny && (
                  <span style={{ fontSize: 10 }}>✨</span>
                )}
                {entry.completed && (
                  <span style={{ fontSize: 10 }}>✅</span>
                )}
              </div>

              <div
                className="pointer-events-none absolute opacity-0 group-hover:opacity-100"
                style={{
                  bottom: `${spriteVisualHeight + 26}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(12,18,28,0.88)',
                  backdropFilter: 'blur(6px)',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '5px 11px',
                  borderRadius: 999,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.32)',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  lineHeight: '16px',
                  transition: 'opacity 0.15s',
                }}
              >
                <strong>{entry.pokemon.realname}</strong>
                {hasNickname && (
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}> «{entry.ownedPokemon.nickname}»</span>
                )}
                <br />
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Nv.{entry.ownedPokemon.level} · {isOwn ? 'Moi' : entry.owner}
                </span>
              </div>
            </div>
          );
        })}

        {entries.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              style={{
                background: 'rgba(255,255,255,0.68)',
                backdropFilter: 'blur(8px)',
                borderRadius: 16,
                padding: '16px 28px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 32 }}>🏡</div>
              <p
                style={{
                  marginTop: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#39576a',
                }}
              >
                La pension est vide
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Stats bar ── */}
      <div
        className="flex flex-wrap items-center gap-3 px-5 py-3"
        style={{ background: '#78350f' }}
      >
        <div className="flex items-center gap-1.5">
          <Users size={13} style={{ color: 'rgba(253,230,138,0.65)' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#fef3c7' }}>
            {total} Pokémon{total > 1 ? 's' : ''} en pension
          </span>
        </div>
        {shinyCount > 0 && (
          <div className="flex items-center gap-1">
            <span style={{ fontSize: 12 }}>✨</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#fde68a' }}>
              {shinyCount} shiny{shinyCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
        {completedCount > 0 && (
          <div className="flex items-center gap-1">
            <CheckCircle2 size={12} style={{ color: '#86efac' }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: '#86efac' }}>
              {completedCount} terminée{completedCount > 1 ? 's' : ''}
            </span>
          </div>
        )}
        <span
          className="ml-auto hidden sm:block"
          style={{ fontSize: 11, color: 'rgba(253,230,138,0.38)' }}
        >
          Survole un Pokémon pour le voir
        </span>
      </div>
    </div>
  );
};

// ─── MyPensionInfo ────────────────────────────────────────────────────────────

interface MyPensionInfoProps {
  pension: MyPension;
  sablierCount: number;
  onWithdraw: () => void;
  isWithdrawing: boolean;
  onSablierUsed: () => void;
}

const MyPensionInfo = ({
  pension,
  sablierCount,
  onWithdraw,
  isWithdrawing,
  onSablierUsed,
}: MyPensionInfoProps) => {
  const { showToast } = useToast();
  const [sablierError, setSablierError] = useState<string | null>(null);
  const msLeft = usePensionCountdown(pension.autoReleaseAt);
  const progress = Math.min(100, (pension.elapsedHours / 24) * 100);
  const { species, isShiny, nickname, level } = pension.pokemon;
  const gifUrl = cdnUrl(isShiny ? species.spriteAnimationShiny : species.spriteAnimation);
  const hasNickname = nickname && nickname !== species.realname;

  const { mutate: doUseSablier, isPending: isUsingSablier } = useMutation({
    mutationFn: useSablier,
    onSuccess: () => {
      setSablierError(null);
      showToast('success', '−1h appliquée grâce aux Sabliers temporels !');
      onSablierUsed();
    },
    onError: (err: Error) => {
      if (err.message.includes('minimum')) {
        setSablierError('Le timer est déjà au minimum.');
      } else {
        showToast('error', err.message);
      }
    },
  });

  const canUseSablier =
    !pension.completed &&
    pension.remainingHours > 0 &&
    sablierCount >= 2 &&
    !sablierError;

  return (
    <div className="overflow-hidden rounded-2xl border border-primary-400/30 bg-surface shadow-sm">
      {/* Orange header band */}
      <div className="flex items-center gap-2 bg-primary-500 px-4 py-2.5">
        <span style={{ fontSize: 13 }}>⭐</span>
        <p className="font-lexend text-sm font-semibold text-white">Mon Pokémon en pension</p>
        {pension.completed && (
          <span className="ml-auto flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold text-white">
            <CheckCircle2 size={10} />
            Terminée !
          </span>
        )}
      </div>

      <div className="flex gap-4 p-4">
        {/* Sprite */}
        <div className="relative shrink-0">
          <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-primary-500/10">
            <img src={gifUrl} alt={nickname} className="h-20 w-20 object-contain" />
          </div>
          {isShiny && (
            <span className="absolute -top-1.5 -right-1.5 text-base">✨</span>
          )}
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="font-lexend text-content font-semibold leading-tight">
              {hasNickname ? nickname : species.realname}
            </p>
            <p className="text-content/50 text-sm">
              {hasNickname ? `${species.realname} · ` : ''}Nv.&nbsp;{level}
            </p>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-raised">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-1000"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{Math.round(pension.elapsedHours)}h écoulées</span>
              {!pension.completed ? (
                <span className="flex items-center gap-1 font-semibold text-content">
                  <Clock size={10} />
                  {formatCountdown(msLeft)}
                </span>
              ) : (
                <span className="font-semibold text-green-600">24h ✓</span>
              )}
            </div>
          </div>

          {/* Revenue */}
          <div className="flex gap-2">
            <div className="flex-1 rounded-xl bg-raised px-3 py-2">
              <p className="text-[10px] text-content/40">Estimé</p>
              <p className="font-lexend text-sm font-semibold text-content">
                {pension.estimatedRevenue.toLocaleString('fr-FR')}&nbsp;₽
              </p>
            </div>
            <div className="flex-1 rounded-xl bg-raised px-3 py-2">
              <p className="text-[10px] text-content/40">Par heure</p>
              <p className="font-lexend text-sm font-semibold text-content">
                ~{(pension.estimatedHourlyRevenue ?? pension.hourlyRevenue).toLocaleString('fr-FR')}&nbsp;💰/h
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sablier button */}
      {!pension.completed && pension.remainingHours > 0 && (
        <div className="px-4 pb-2">
          {sablierError ? (
            <p className="text-center text-xs text-red-500">{sablierError}</p>
          ) : sablierCount < 2 ? (
            <p className="text-content/40 text-center text-xs">
              Il vous faut au moins 2 Sabliers temporels pour avancer le timer.
            </p>
          ) : (
            <CustomButton
              title="⏳ Utiliser 2 Sabliers (−1h)"
              color="background"
              size="sm"
              rounded="full"
              fullWidth
              loading={isUsingSablier}
              disabled={!canUseSablier}
              onClick={() => doUseSablier()}
            />
          )}
        </div>
      )}

      <div className="border-t border-line-soft px-4 pb-4 pt-3">
        <CustomButton
          title="Récupérer mon Pokémon"
          color="secondary"
          size="sm"
          rounded="full"
          fullWidth
          loading={isWithdrawing}
          onClick={onWithdraw}
        />
      </div>
    </div>
  );
};

// ─── DepositModal ─────────────────────────────────────────────────────────────

interface DepositModalProps {
  onClose: () => void;
  onDeposit: (pokemon: OwnedPokemon) => void;
  isDepositing: boolean;
  error: string | null;
}

const DepositModal = ({ onClose, onDeposit, isDepositing, error }: DepositModalProps) => {
  const [selected, setSelected] = useState<OwnedPokemon | null>(null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-page flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl shadow-2xl sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line-soft bg-surface px-5 py-4">
          <h2 className="font-lexend text-content font-semibold">Déposer un Pokémon</h2>
          <button
            onClick={onClose}
            className="text-content/40 hover:text-content rounded-lg p-1.5 transition-colors hover:bg-raised"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <PokemonPicker
            onSelect={setSelected}
            selectedId={selected?.ownedPokemonId}
            loading={isDepositing}
            queryKey="pension-deposit"
          />
        </div>

        {error && (
          <div className="mx-4 rounded-xl bg-red-500/10 px-4 py-3">
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        )}

        <div className="border-t border-line-soft bg-surface p-4">
          <SelectedPokemonPanel
            selected={selected}
            loading={isDepositing}
            onConfirm={() => selected && onDeposit(selected)}
          />
        </div>
      </div>
    </div>
  );
};

// ─── WithdrawRecapModal ───────────────────────────────────────────────────────

interface WithdrawRecapModalProps {
  result: WithdrawPensionResponse;
  onClose: () => void;
}

const WithdrawRecapModal = ({ result, onClose }: WithdrawRecapModalProps) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-surface shadow-2xl">
      <div className="bg-primary-500 px-6 py-5 text-center">
        <p className="font-lexend text-2xl font-bold text-white">
          +{result.pokeDollarsEarned.toLocaleString('fr-FR')}&nbsp;₽
        </p>
        <p className="mt-1 text-sm text-white/80">PokéDollars gagnés</p>
      </div>

      <div className="space-y-3 p-5">
        {result.completed ? (
          <>
            <div className="flex items-center gap-2 rounded-xl bg-green-500/10 px-4 py-3">
              <CheckCircle2 size={16} className="shrink-0 text-green-600" />
              <p className="text-sm font-medium text-green-700">24h complètes — bonus appliqués !</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-raised px-3 py-2.5 text-center">
                <p className="text-content/50 text-xs">Bonheur</p>
                <p className="text-content font-lexend font-semibold">+{result.happinessGained}</p>
              </div>
              {result.leveledUp && (
                <div className="rounded-xl bg-yellow-500/10 px-3 py-2.5 text-center">
                  <p className="text-content/50 text-xs">Niveau</p>
                  <p className="text-content font-lexend font-semibold">+1 ✨</p>
                </div>
              )}
            </div>
            {result.itemDropped && (
              <div className="flex items-center gap-2 rounded-xl bg-purple-500/10 px-4 py-3">
                <Package size={15} className="shrink-0 text-purple-600" />
                <div>
                  <p className="text-xs text-purple-500">Objet reçu</p>
                  <p className="text-sm font-semibold text-purple-700">{result.itemDropped.name}</p>
                </div>
              </div>
            )}
            {result.cellDropped && (
              <div className="flex items-center gap-2 rounded-xl bg-green-500/10 px-4 py-3">
                <span className="text-base">🔬</span>
                <div>
                  <p className="text-xs text-green-600">Cellule Zygarde obtenue !</p>
                  <p className="text-sm font-semibold text-green-700">+1 Cellule Zygarde</p>
                </div>
              </div>
            )}
            {result.retrievedLate && (
              <div className="flex items-center gap-2 rounded-xl bg-yellow-500/10 px-4 py-3">
                <AlertCircle size={15} className="shrink-0 text-yellow-600" />
                <p className="text-sm text-yellow-600 dark:text-yellow-400">Récupéré en retard — quelques bonus manqués.</p>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl bg-raised px-4 py-3">
            <p className="text-content/60 text-sm">Pas de bonus — retrait avant 24h.</p>
          </div>
        )}

        <CustomButton
          title={result.evolution ? 'Continuer →' : result.pendingMove ? 'Continuer →' : 'Fermer'}
          color="primary"
          fullWidth
          rounded="full"
          onClick={onClose}
        />
      </div>
    </div>
  </div>
);

// ─── PensionEntryCard ─────────────────────────────────────────────────────────

interface PensionEntryCardProps {
  entry: PensionEntry;
  isOwn: boolean;
  onWithdraw: () => void;
  isWithdrawing: boolean;
}

const PensionEntryCard = ({ entry, isOwn, onWithdraw, isWithdrawing }: PensionEntryCardProps) => {
  const { pokemon, ownedPokemon } = entry;
  const spriteUrl = cdnUrl(ownedPokemon.isShiny ? pokemon.spriteShiny : pokemon.sprite);
  const hasNickname = ownedPokemon.nickname && ownedPokemon.nickname !== pokemon.realname;

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border bg-surface p-3 shadow-sm transition-shadow hover:shadow-md ${
        isOwn ? 'border-primary-400/30' : 'border-transparent'
      }`}
    >
      <div className="bg-primary-500/10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl">
        <img
          src={spriteUrl}
          alt={ownedPokemon.nickname}
          loading="lazy"
          className="h-12 w-12 object-contain"
          onError={e => { e.currentTarget.style.opacity = '0'; }}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-lexend text-content truncate font-semibold text-sm">
            {pokemon.realname}
          </p>
          {hasNickname && (
            <span className="text-content/40 truncate text-xs">«{ownedPokemon.nickname}»</span>
          )}
          {ownedPokemon.isShiny && (
            <span className="text-xs">✨</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="bg-secondary-500 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white">
            Nv.&nbsp;{ownedPokemon.level}
          </span>
        </div>
        <p className="text-content/40 mt-1 text-xs">
          {entry.completed ? (
            <span className="font-medium text-green-600">Terminée !</span>
          ) : (
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {entry.remainingHours}h restantes
            </span>
          )}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-2">
        <span className={`text-xs ${isOwn ? 'text-primary-500 font-semibold' : 'text-content/40'}`}>
          {isOwn ? 'Moi' : entry.owner}
        </span>
        {isOwn && (
          <CustomButton
            title="Récupérer"
            color="secondary"
            size="sm"
            rounded="full"
            loading={isWithdrawing}
            onClick={onWithdraw}
          />
        )}
      </div>
    </div>
  );
};

// ─── Sort options ─────────────────────────────────────────────────────────────

const SORT_OPTIONS: { value: AllPensionsSortBy; label: string }[] = [
  { value: 'depositedAt', label: 'Date de dépôt' },
  { value: 'level', label: 'Niveau' },
  { value: 'name', label: 'Nom' },
  { value: 'rarity', label: 'Rareté' },
];

// ─── Pension (main view) ──────────────────────────────────────────────────────

export const Pension = () => {
  const queryClient = useQueryClient();
  const { meUser } = useMeUser();
  const { showToast } = useToast();

  const { data: myPension, isLoading: isMyPensionLoading } = useQuery({
    queryKey: ['myPension'],
    queryFn: getMyPension,
  });

  // Capture pension data before withdraw (needed for LearnMoveModal attacks)
  const capturedPensionRef = useRef<MyPension | null>(null);

  const { data: inventoryData } = useQuery({
    queryKey: ['inventory-pension'],
    queryFn: () => getInventory({ offset: 0, limit: 100 }),
    staleTime: 60_000,
  });
  const sablierCount = inventoryData?.data.find(i => i.itemId === 17)?.quantity ?? 0;

  const [allOffset, setAllOffset] = useState(0);
  const [allLimit, setAllLimit] = useState<25 | 50 | 100>(25);
  const [allSortBy, setAllSortBy] = useState<AllPensionsSortBy>('depositedAt');
  const [allSortOrder, setAllSortOrder] = useState<AllPensionsSortOrder>('DESC');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setAllOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: allPensions, isLoading: isAllLoading } = useQuery({
    queryKey: ['allPensions', allOffset, allLimit, allSortBy, allSortOrder, debouncedSearch],
    queryFn: () =>
      getAllPensions({
        offset: allOffset,
        limit: allLimit,
        sortBy: allSortBy,
        sortOrder: allSortOrder,
        search: debouncedSearch || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const total = allPensions?.total ?? 0;
  const totalPages = Math.ceil(total / allLimit);
  const currentPage = Math.floor(allOffset / allLimit) + 1;

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [withdrawResult, setWithdrawResult] = useState<WithdrawPensionResponse | null>(null);
  const [capturedPension, setCapturedPension] = useState<MyPension | null>(null);
  const [pendingEvolution, setPendingEvolution] = useState<WithdrawPensionResponse['evolution']>(undefined);
  const [pendingMove, setPendingMove] = useState<WithdrawPensionResponse['pendingMove']>(undefined);

  const { mutate: deposit, isPending: isDepositing } = useMutation({
    mutationFn: (pokemon: OwnedPokemon) => depositPokemon(pokemon.ownedPokemonId),
    onSuccess: () => {
      setShowDepositModal(false);
      setDepositError(null);
      queryClient.invalidateQueries({ queryKey: ['myPension'] });
      queryClient.invalidateQueries({ queryKey: ['allPensions'] });
      showToast('success', 'Pokémon déposé à la pension !');
    },
    onError: (err: Error) => {
      setDepositError(err.message);
    },
  });

  const { mutate: withdraw, isPending: isWithdrawing } = useMutation({
    mutationFn: withdrawPokemon,
    onSuccess: data => {
      setCapturedPension(capturedPensionRef.current);
      setWithdrawResult(data);
      queryClient.invalidateQueries({ queryKey: ['myPension'] });
      queryClient.invalidateQueries({ queryKey: ['allPensions'] });
    },
    onError: (err: Error) => {
      showToast('error', err.message);
    },
  });

  const handleWithdraw = () => {
    capturedPensionRef.current = myPension ?? null;
    withdraw();
  };

  const handleRecapClose = () => {
    const result = withdrawResult;
    setWithdrawResult(null);
    if (result?.evolution) {
      setPendingEvolution(result.evolution);
    } else if (result?.pendingMove) {
      setPendingMove(result.pendingMove);
    }
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const resetFilters = (
    updates: Partial<{
      sortBy: AllPensionsSortBy;
      sortOrder: AllPensionsSortOrder;
      limit: 25 | 50 | 100;
    }>,
  ) => {
    setAllOffset(0);
    if (updates.sortBy !== undefined) setAllSortBy(updates.sortBy);
    if (updates.sortOrder !== undefined) setAllSortOrder(updates.sortOrder);
    if (updates.limit !== undefined) setAllLimit(updates.limit);
  };

  const pensionPokemon = capturedPension?.pokemon;

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-5">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-lexend text-content text-2xl font-bold">La Pension</h1>
            <p className="text-content/50 mt-0.5 text-sm">
              Dépose un Pokémon pour gagner des PokéDollars pendant 24h
            </p>
          </div>
          {!myPension && !isMyPensionLoading && (
            <CustomButton
              title="Déposer"
              color="primary"
              rounded="full"
              onClick={() => setShowDepositModal(true)}
            />
          )}
        </div>

        {/* ── Paddock ── */}
        {isAllLoading && !allPensions ? (
          <div
            className="flex items-center justify-center rounded-3xl bg-white/60"
            style={{ height: 380, border: '4px solid #92400e' }}
          >
            <Loader size={40} color="#f29602" />
          </div>
        ) : (
          <PensionPaddock
            entries={allPensions?.data ?? []}
            total={total}
            meUserId={meUser?.userId}
          />
        )}

        {/* ── My pension info / empty CTA ── */}
        {isMyPensionLoading ? (
          <div className="flex justify-center py-4">
            <Loader size={32} color="#f29602" />
          </div>
        ) : myPension ? (
          <MyPensionInfo
            pension={myPension}
            sablierCount={sablierCount}
            onWithdraw={handleWithdraw}
            isWithdrawing={isWithdrawing}
            onSablierUsed={() => {
              queryClient.invalidateQueries({ queryKey: ['myPension'] });
              queryClient.invalidateQueries({ queryKey: ['inventory-pension'] });
            }}
          />
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white/60 py-8 text-center">
            <p className="text-3xl">🏠</p>
            <p className="text-content/60 text-sm">
              Tu n'as pas encore de Pokémon en pension
            </p>
            <CustomButton
              title="Déposer un Pokémon"
              color="primary"
              rounded="full"
              onClick={() => setShowDepositModal(true)}
            />
          </div>
        )}

        {/* ── Active pensions list ── */}
        <GenericCard
          title="Pensions actives"
          subtitle={total > 0 ? `${total} Pokémon${total > 1 ? 's' : ''} en pension` : undefined}
        >
          {/* Filters */}
          <div className="mb-4 flex flex-wrap gap-2">
            <div className="relative min-w-0 flex-1" style={{ minWidth: '160px' }}>
              <Search
                size={14}
                className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
              />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Rechercher..."
                className="text-content placeholder:text-content/40 focus:ring-primary-400 h-9 w-full rounded-xl border border-line-soft bg-raised pr-8 pl-8 text-sm focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            <select
              value={allSortBy}
              onChange={e => resetFilters({ sortBy: e.target.value as AllPensionsSortBy })}
              className="text-content h-9 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              onClick={() =>
                resetFilters({ sortOrder: allSortOrder === 'ASC' ? 'DESC' : 'ASC' })
              }
              className="text-content/60 hover:text-content flex h-9 items-center gap-1 rounded-xl border border-line-soft bg-raised px-3 text-sm transition-colors hover:bg-line-soft"
              title={allSortOrder === 'ASC' ? 'Croissant' : 'Décroissant'}
            >
              {allSortOrder === 'ASC' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              <span className="hidden sm:inline">
                {allSortOrder === 'ASC' ? 'Croissant' : 'Décroissant'}
              </span>
            </button>

            <div className="flex gap-1">
              {([25, 50, 100] as const).map(pageSize => (
                <button
                  key={pageSize}
                  type="button"
                  onClick={() => resetFilters({ limit: pageSize })}
                  className={`h-9 rounded-xl px-3 text-sm font-semibold transition-colors ${
                    allLimit === pageSize
                      ? 'bg-primary-500 text-white'
                      : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                  }`}
                >
                  {pageSize}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          {isAllLoading ? (
            <div className="flex justify-center py-12">
              <Loader size={36} color="#f29602" />
            </div>
          ) : !allPensions || allPensions.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <p className="text-5xl">🔍</p>
              <p className="text-content/50 text-sm">
                {debouncedSearch
                  ? `Aucune pension trouvée pour «\u00A0${debouncedSearch}\u00A0»`
                  : 'Aucune pension active pour le moment'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {allPensions.data.map(entry => (
                <PensionEntryCard
                  key={entry.pensionId}
                  entry={entry}
                  isOwn={entry.userId === meUser?.userId}
                  onWithdraw={handleWithdraw}
                  isWithdrawing={isWithdrawing}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-1.5">
              <CustomButton
                icon={<ChevronLeft size={16} />}
                iconOnly
                color="white"
                rounded="full"
                disabled={allOffset === 0}
                onClick={() => {
                  setAllOffset(Math.max(0, allOffset - allLimit));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => {
                    setAllOffset((page - 1) * allLimit);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    page === currentPage
                      ? 'bg-primary-500 text-white'
                      : 'text-content/60 hover:bg-raised'
                  }`}
                >
                  {page}
                </button>
              ))}
              <CustomButton
                icon={<ChevronRight size={16} />}
                iconOnly
                color="white"
                rounded="full"
                disabled={allOffset + allLimit >= total}
                onClick={() => {
                  setAllOffset(allOffset + allLimit);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}
        </GenericCard>
      </div>

      {/* ── Modals ── */}
      {showDepositModal && (
        <DepositModal
          onClose={() => {
            setShowDepositModal(false);
            setDepositError(null);
          }}
          onDeposit={deposit}
          isDepositing={isDepositing}
          error={depositError}
        />
      )}

      {withdrawResult && (
        <WithdrawRecapModal
          result={withdrawResult}
          onClose={handleRecapClose}
        />
      )}

      {pendingEvolution && (
        <EvolutionModal
          evolution={pendingEvolution}
          onDone={() => {
            setPendingEvolution(undefined);
            setCapturedPension(null);
          }}
        />
      )}

      {pendingMove && pensionPokemon && (
        <LearnMoveModal
          ownedPokemonId={capturedPension?.pokemon.ownedPokemonId ?? ''}
          pokemonName={pensionPokemon.species?.realname ?? pensionPokemon.nickname}
          pendingMove={pendingMove}
          currentAttacks={pensionPokemon.attacks}
          onDone={() => {
            setPendingMove(undefined);
            setCapturedPension(null);
          }}
        />
      )}
    </main>
  );
};
